import {
  EducationProgressionPromptDto,
  useAdjustEducationProgressionPrompt,
  useConfirmEducationProgressionPrompt,
  useDismissEducationProgressionPrompt,
} from 'hooks/api/backend/user/education-progression';
import { useUser } from 'utils/auth';
import { queryClient } from 'utils/react-query';

type EducationProgressionModalHandlers = {
  onAdjust: ReturnType<typeof useAdjustEducationProgressionPrompt>['mutateAsync'];
  onConfirm: () => Promise<void>;
  onDismiss: () => Promise<void>;
  isAdjusting: boolean;
  isConfirming: boolean;
  isDismissing: boolean;
};

export const useEducationProgressionModalHandlers = (
  prompt: EducationProgressionPromptDto | null
): EducationProgressionModalHandlers => {
  const { refetch: refetchUser } = useUser();
  const adjustMutation = useAdjustEducationProgressionPrompt();
  const confirmMutation = useConfirmEducationProgressionPrompt();
  const dismissMutation = useDismissEducationProgressionPrompt();
  const promptId = prompt?.id ?? '';

  const invalidatePrompt = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['education-progression-prompt'] });
    await refetchUser();
  };

  return {
    onAdjust: async (data) => {
      await adjustMutation.mutateAsync(data);
      await invalidatePrompt();
    },
    onConfirm: async () => {
      if (!promptId) {
        return;
      }

      await confirmMutation.mutateAsync({ id: promptId });
      await invalidatePrompt();
    },
    onDismiss: async () => {
      if (!promptId) {
        return;
      }

      await dismissMutation.mutateAsync({ id: promptId });
      await invalidatePrompt();
    },
    isAdjusting: adjustMutation.isPending,
    isConfirming: confirmMutation.isPending,
    isDismissing: dismissMutation.isPending,
  };
};
